import React from 'react';
import { Alert } from 'reactstrap';

const recordNames = {
    MachineModel: 'Modelo', Brand: 'Marca', AppCode: 'Código de aplicación', AppCodeMethod: 'Vinculación de método',
    Exam: 'Examen', ExamMethod: 'Método de examen', ExamMethodMachineModel: 'Asociación con el modelo',
    AnalyteAgeGenderRange: 'Rango por edad y género', Analyte: 'Analito', InfoAnalyte: 'Analito del catálogo',
    Method: 'Método', AnalyteNumber: 'Configuración numérica', AnalyteRange: 'Rango personalizado', Gender: 'Género',
    PrecodedText: 'Texto precodificado', TypePrecodedText: 'Tipo de texto precodificado',
    AnalyteNumberCustomValue: 'Valor personalizado', MachineModelExcludeAppCode: 'Exclusión de interfaz'
};
const name = entity => recordNames[entity] || 'Registro';
const label = (entity, id) => id == null ? 'Sin registro asignado' : `${name(entity)} #${id}`;

// Support the previous API response during independent frontend/backend deployments.
const missingDescriptions = {
    Exam: ['Examen no disponible', 'El método está asociado a un examen que no aparece en la configuración vigente.', 'Revisa el examen indicado y su método en Diseño de exámenes.'],
    ExamMethod: ['Método de examen no disponible para copiar', 'El método asociado no está disponible dentro de la configuración seleccionada para copiar.', 'Revisa el método de examen y su vinculación con el código de aplicación del origen. Puede estar eliminado o no pertenecer a esa configuración.'],
    AppCodeMethod: ['Vinculación de método no disponible', 'La vinculación entre el método y el código de aplicación no está disponible para copiar.', 'Revisa el método del rango y su código en Vinculación de analitos.'],
    Analyte: ['Analito no disponible', 'No se encontró el analito asociado o de referencia en la configuración vigente.', 'Revisa el analito del rango y sus referencias en Diseño de exámenes.'],
    InfoAnalyte: ['Analito del catálogo no disponible', 'El código utiliza un analito que no aparece en el catálogo vigente.', 'Revisa Analitos y su vinculación con el código de aplicación.']
};
const titles = {
    no_active_exam: 'Método sin exámenes para copiar', no_active_method: 'Código sin métodos activos',
    no_active_ranges: 'Método de examen sin rangos de referencia', empty_range: 'Rango sin valores ni alertas',
    inactive_parent: 'Rango excluido por un método inactivo', manual_method_excluded: 'Método manual fuera del alcance de la copia',
    interface_exclusion_not_copied: 'Regla de exclusión de interfaz no incluida', target_not_active: 'El modelo de destino está inactivo',
    destination_app_code: 'El destino ya tiene este código o analito', duplicate_app_code: 'Hay códigos o analitos repetidos',
    method_mismatch: 'Los métodos vinculados no coinciden', range_method_mismatch: 'El rango apunta a dos métodos diferentes',
    range_analyte_mismatch: 'El rango no corresponde al analito o al examen', custom_range_mismatch: 'El valor personalizado corresponde a otro analito',
    machine_link_mismatch: 'La asociación no corresponde al modelo de origen', preview_changed: 'La configuración cambió después de revisarla'
};

const presentIssue = issue => {
    const legacyReference = issue.code === 'missing_reference' && (issue.message || '').match(/^Falta la referencia vigente a (.+)\.$/);
    const relatedEntity = issue.related_entity || (legacyReference ? legacyReference[1].split(' ')[0] : null);
    const missing = issue.code === 'missing_reference' && missingDescriptions[relatedEntity];
    return {
        ...issue,
        related_entity: relatedEntity,
        title: issue.title || (missing && missing[0]) || titles[issue.code] || 'Configuración que requiere revisión',
        explanation: issue.title ? issue.message : (missing && missing[1]) || issue.message,
        action: issue.suggested_action || (missing && missing[2]) || (issue.code === 'no_active_exam'
            ? 'Revisa si este método debe estar asociado a un examen. Si aún no se utiliza, confirma si es correcto copiarlo sin exámenes asociados.'
            : 'Solicita al responsable de Diseño revisar los registros indicados. Después genera una nueva vista previa.'),
        source_label: issue.source_label || label(issue.entity, issue.source_id),
        related_label: issue.related_label || (relatedEntity || issue.related_id != null ? label(relatedEntity, issue.related_id) : null)
    };
};

export const groupConfigurationCopyIssues = issues => {
    const groups = new Map();
    issues.forEach(original => {
        const issue = presentIssue(original);
        // Group by problem type, never discard individual records or identifiers.
        const key = JSON.stringify([issue.code, issue.entity, issue.related_entity, issue.title, issue.explanation, issue.action]);
        if (!groups.has(key)) groups.set(key, { key, ...issue, issues: [] });
        groups.get(key).issues.push(issue);
    });
    return Array.from(groups.values());
};

export const ConfigurationCopyIssues = ({ issues, conflict = false }) => {
    if (!issues || !issues.length) return null;
    const groups = groupConfigurationCopyIssues(issues);
    return <Alert color={conflict ? 'danger' : 'warning'} className="configuration-copy__issues" role="alert">
        <h5>{conflict ? 'Hay problemas que impiden realizar la copia' : 'Advertencias para revisar antes de copiar'}</h5>
        <p><strong>{issues.length} {issues.length === 1 ? 'incidencia' : 'incidencias'}</strong>, {groups.length === 1 ? 'de un tipo de problema' : `agrupadas en ${groups.length} tipos de problema`}.</p>
        <p>{conflict
            ? 'La copia está bloqueada. Revisa los registros señalados con el responsable de Diseño y después actualiza la vista previa. La herramienta no corrige ni elimina la configuración existente.'
            : 'Estas advertencias no bloquean por sí solas la copia. Indican qué configuración está incompleta o quedará fuera del proceso.'}</p>
        <ol className="configuration-copy__issue-groups">
            {groups.map(group => {
                const relatedLabels = [...new Set(group.issues.map(issue => issue.related_label).filter(Boolean))];
                return <li key={group.key} className="configuration-copy__issue-group">
                    <div className="configuration-copy__issue-heading"><h6>{group.title}</h6><span>{group.issues.length} {group.issues.length === 1 ? 'incidencia' : 'incidencias'}</span></div>
                    <p>{group.explanation}</p>
                    {relatedLabels.length === 1 && <p><strong>Vínculo por revisar:</strong> {relatedLabels[0]}</p>}
                    <p className="configuration-copy__issue-action"><strong>Qué revisar: </strong>{group.action}</p>
                    <details open={group.issues.length === 1}>
                        <summary>Ver registros afectados ({group.issues.length})</summary>
                        <ul className="configuration-copy__issue-records">
                            {group.issues.map((issue, index) => <li key={`${issue.source_id}-${issue.related_id}-${index}`}>
                                <p className="mb-1"><strong>{issue.source_label}</strong></p>
                                {relatedLabels.length > 1 && issue.related_label && <p className="mb-1">Vínculo por revisar: {issue.related_label}</p>}
                                <details>
                                    <summary>Detalles para soporte</summary>
                                    <div className="configuration-copy__technical">
                                        <span>Tipo de registro: {name(issue.entity)} ({issue.entity})</span>
                                        {issue.source_id != null && <span>ID de origen: {issue.source_id}</span>}
                                        {issue.related_id != null && <span>ID relacionado: {issue.related_id}</span>}
                                        <span>Código de diagnóstico: {issue.code}</span>
                                        {issue.related_entity && <span>Tipo de vínculo: {issue.related_entity}</span>}
                                        {issue.message !== issue.explanation && <span>Mensaje del servidor: {issue.message}</span>}
                                    </div>
                                </details>
                            </li>)}
                        </ul>
                    </details>
                </li>;
            })}
        </ol>
    </Alert>;
};
